/**
 * =================================================================================================
 * >>> **THE GO-LIVE SAFETY CHECK. HER ORDER, 2026-09-13.** <<<
 *
 * *"when she taps go live, her screen shows BIG and clear: GATE DROP: ON or GATE DROP: OFF, tap to
 * drop it first, so she physically cannot go live with the gate up by accident. That single guard
 * prevents the one failure that would wall 2,966 people."*
 *
 * **THE FAILURE IT PREVENTS IS MEASURED, NOT IMAGINED.** On 2026-09-13 the house went live four
 * times. Session 55 called 3,384 members. The gate drop had never been tapped in the life of the
 * table, `room:living` refused free seats 61 times in the hour, and **every single viewer who got in
 * across all four sessions was a paying `vet`**. 2,966 of the people called could not have entered
 * had they tried. Nothing was broken; the gate was simply up, and nothing on her screen said so.
 *
 * **THREE STATES, NEVER TWO (R18), AND THE THIRD IS THE ONE THAT MATTERS.** *Open*, *closed*, and
 * **we could not find out**. A screen that renders "could not read the status" as ON would hand her
 * a green light on the one question this guard exists to answer, so an unreadable status is treated
 * as NOT SAFE and says so in its own words. The safe direction here is the annoying one.
 *
 * **IT BLOCKS BY DEFAULT AND YIELDS TO A DELIBERATE ACT.** Her word is *by accident*, so this may
 * never become a wall she cannot pass: a live she genuinely wants to run with the gate up is hers to
 * run, and it takes one explicit tick that cannot be hit by muscle memory. What it may not be is
 * silent.
 *
 * **THIS MODULE IMPORTS NOTHING**, so a FAST fixture LOADS and RUNS every law in it (R146).
 * =================================================================================================
 */

/** What the banner is. Three, because *open*, *shut* and *unknown* are three different sentences. */
export const GATE_BANNER = Object.freeze({ on: "on", off: "off", unknown: "unknown", notHers: "not-hers" });

/** The words on her screen, declared here so the screen cannot invent its own (R28/R29). */
export const GATE_BANNER_TEXT = Object.freeze({
  on: "GATE DROP: ON",
  off: "GATE DROP: OFF",
  unknown: "GATE DROP: CANNOT TELL",
  // A reader's screen prints nothing at all, because the gate is not her business (see GATE_NOT_HERS).
  "not-hers": "",
});

export const GATE_BANNER_SUB = Object.freeze({
  on: "Every member can watch, including free accounts.",
  off: "Tap to drop it first, or free members cannot get in.",
  unknown: "The gate could not be read just now. Drop it to be sure, or check before you start.",
  "not-hers": "",
});

/**
 * >>> **THE DROP IS A TIMED WINDOW, AND THAT IS THE SECOND WAY THIS GOES WRONG.** <<<
 *
 * Her plan, 2026-09-13: *"I'm gonna drop the gate while I get my live ready."* Dropping it during
 * setup starts the clock during setup. On the house default of 60 minutes, twenty-five minutes of
 * getting ready plus an hour on air means **the gate raises itself while she is still talking**, and
 * everybody who arrives late is turned away by a house that was open when she announced it. That is
 * the same failure as going live with the gate up, arriving twenty minutes late.
 *
 * So the screen asks for a window sized for *drop now, talk later* rather than the one-tap default,
 * and it says out loud how long is left instead of leaving her to guess.
 */
export const GATE_DROP_PREP_MINUTES = 180;

/** Under this, the gate is open but is not going to stay open long enough to trust. */
export const GATE_LOW_MINUTES = 25;

/** Whole minutes left, or null where the status does not say. Presence, never coercion. */
export function gateMinutesLeft(status) {
  const raw = status && typeof status === "object" ? status.secondsLeft : null;
  const n = typeof raw === "number" && Number.isFinite(raw) ? raw : null;
  return n === null ? null : Math.max(0, Math.floor(n / 60));
}

/** "2h 5m left" / "18m left" / "" — the countdown as her screen prints it. */
export function gateTimeLeftText(minutes) {
  if (minutes === null || minutes === undefined) return "";
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return `${m}m left`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${rest}m left` : `${h}h left`;
}

/**
 * >>> **WHAT THE GO-LIVE SCREEN MUST SHOW AND WHETHER IT MAY START.** <<<
 *
 * Takes the answer from `GET /api/event/drop/status` exactly as it arrives, including the shapes
 * that are not answers at all: `null` from a failed fetch, a body that is not an object, a payload
 * whose `ok` is false. **PRESENCE, NEVER TRUTHINESS (R53/R136/R137)** — `open` comes off a JSON
 * payload where `"false"` is a truthy string, so only a real boolean `true` is open.
 *
 * `acknowledged` is her deliberate tick. It can turn a refusal into a yes and it can never turn the
 * banner green: the screen keeps saying OFF while she goes, which is the honest thing to leave on a
 * screen she may be recording.
 */
export function goLiveGuard(status, { acknowledged = false } = {}) {
  const open = status && typeof status === "object" && status.ok !== false && status.open === true;
  const readable = status && typeof status === "object" && status.ok !== false
    && (typeof status.open === "boolean");

  const banner = open ? GATE_BANNER.on : (readable ? GATE_BANNER.off : GATE_BANNER.unknown);
  const ack = acknowledged === true;

  // The clock, and it only means anything while the gate is actually open.
  const minutesLeft = open ? gateMinutesLeft(status) : null;
  const timeLeft = open ? gateTimeLeftText(minutesLeft) : "";
  // **RUNNING OUT IS NOT THE SAME AS SHUT (R18).** She may absolutely go live on a gate with twelve
  // minutes left, and she should be told, because the people who arrive in minute thirteen are the
  // ones who will be turned away from a house she announced as open.
  const runningLow = open && minutesLeft !== null && minutesLeft < GATE_LOW_MINUTES;

  return {
    banner,
    text: GATE_BANNER_TEXT[banner],
    sub: banner === GATE_BANNER.on && timeLeft
      ? (runningLow
        ? `${timeLeft}. It will raise itself while you are still on air, and late arrivals will be turned away.`
        : `Every member can watch, including free accounts. ${timeLeft}.`)
      : GATE_BANNER_SUB[banner],
    minutesLeft,
    timeLeft,
    runningLow,
    // Offer the extend where extending is the repair: open, but not for long enough.
    offerExtend: runningLow,
    // The gate is genuinely open: nothing to warn about and nothing to tick.
    safe: banner === GATE_BANNER.on,
    // >>> **SHOULD THE SCREEN DRESS ITSELF AS A WARNING?** <<< The button's class and its label both
    // asked `banner === GATE_BANNER.on` in their own words, which is the same law spelled three
    // times (R28/R29) — and it is the spelling that breaks the moment a FOURTH banner state exists.
    warn: banner !== GATE_BANNER.on,
    // May the broadcast start? Open, or she has deliberately said go anyway.
    mayGoLive: banner === GATE_BANNER.on || ack,
    // Should the screen offer the one-tap drop? Only where dropping is the repair.
    offerDrop: banner !== GATE_BANNER.on,
    // Should the screen ask for the tick? Only while it is still blocking her.
    needsAcknowledgement: banner !== GATE_BANNER.on && !ack,
    // R18: WHY she is being held, in words the screen can print without composing its own.
    why: banner === GATE_BANNER.on ? ""
      : banner === GATE_BANNER.off
        ? "The gate is up. Free members will be turned away at the door."
        : "The gate's state could not be read, so this cannot promise anybody will get in.",
  };
}

/**
 * =================================================================================================
 * >>> **THE GATE IS THE OWNER'S, AND A READER MAY NEVER BE HELD BY IT. HER REPORT, 2026-09-18.** <<<
 *
 * *"It also mentioned something about a Gate and the option to lower it before he started his live?!
 * this should not be showing on customer accounts."*
 *
 * **IT WAS WORSE THAN SHOWING.** `mayGoLive` disables the go-live button until the gate reads OPEN
 * or she ticks the box — and the gate is normally SHUT, so a reader tapping *go live* met a banner
 * about house machinery, a **disabled** button relabelled *"Go live with the gate UP"*, and a tick
 * box asking her to override a thing she has never heard of and cannot drop (the drop route is
 * owner-only, and always was). She could not go on air at all without accepting a warning that was
 * never about her.
 *
 * **SO THE GUARD IS ENTERED ONLY ON THE OWNER'S SEAT**, and on every other seat this is the verdict:
 * no banner, no drop, no tick, nothing to warn about, and **`mayGoLive: true`** — because the one
 * failure this guard exists to prevent is the OWNER calling 3,384 members into a shut room, and a
 * reader going live calls nobody.
 *
 * **THE ENTRY-CONDITION AUDIT SAYS THIS IS THE DANGEROUS KIND OF CHANGE**, since `owner` is exactly
 * an entry condition that switches a correct law off. It is therefore DRIVEN from both sides in
 * `scripts/golive-guard.mjs`: the owner on a shut gate is still refused, and the reader on the same
 * shut gate still goes live.
 * =================================================================================================
 */
export const GATE_NOT_HERS = Object.freeze({
  banner: GATE_BANNER.notHers,
  text: "",
  sub: "",
  minutesLeft: null,
  timeLeft: "",
  runningLow: false,
  offerExtend: false,
  safe: true,
  warn: false,
  mayGoLive: true,
  offerDrop: false,
  needsAcknowledgement: false,
  why: "",
});

/**
 * The one door the screen calls, so the owner test lives beside the law rather than in a component
 * that would have to remember it at four separate call sites (R28/R29).
 *
 * **`owner` MUST BE A REAL `true`** (R53/R136/R137). Anything else is a reader, which is the
 * direction that cannot hurt: a reader wrongly treated as the owner would merely be shown a banner,
 * while the owner wrongly treated as a reader is 3,384 people walled out in silence.
 */
export function goLiveGuardFor(status, { acknowledged = false, owner = false } = {}) {
  return owner === true ? goLiveGuard(status, { acknowledged }) : GATE_NOT_HERS;
}

/**
 * The label on the button that starts the broadcast. It CHANGES when the gate is up, because a
 * button that reads the same in both states is the button she taps without looking (the whole of
 * this guard in one sentence).
 */
export function goLiveButtonLabel(guard, { starting = false } = {}) {
  if (starting === true) return "Going live…";
  // `warn` rather than the banner, so a reader's verdict (which has no banner) reads plainly.
  if (!guard || guard.warn !== true) return "Go live now";
  return "Go live with the gate UP";
}
